import type { OnInit } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

import {
    EButtonStyleType,
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
} from '@common/enums';

import type { ButtonEntity, ButtonItem } from '@sites/entity-core';
import type { ButtonForm, ButtonZeroForm } from '@sites/entity-signal-form';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    DEFAULT_BUTTON_CLASS,
    BUTTON_TYPE_CLASS_MAP,
    BUTTON_SIZE_CLASS_MAP,
} from '@web/widgets/common/base/base-button/base-button.const';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import {
    BUTTON_OUTER_MARGIN,
    CONTAINER_INNER_MARGIN,
    getButtonItemCssClasses,
    getButtonItemCssVars,
    type ButtonCssVars,
} from '@sites/entity-core/src/widget/button/styles/button.css-vars';
import { flexAlignVar, rotateVar } from '@sites/entity-core';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

/**
 * Editor preview for the `button` widget — fully signal-based, the button
 * twin of the migrated video view. The form is shared with the control panel
 * via `WebLegacyWidgetSignalFormService` (ref-counted by widget id).
 */
@Component({
    selector: 'web-button-view',
    templateUrl: './button-view.component.html',
    styleUrls: ['./button-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
})
export class ButtonViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<ButtonEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef = inject(ElementRef);
    private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public form!: ButtonForm;

    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    /**
     * True when the widget lives inside a zero block. Read through `value()`
     * — NOT `form.type()`: the `type` FieldTree child only materialises when
     * the key exists in the model, and default-variant entities omit it.
     */
    public get isZero(): boolean {
        return this.form?.value().type === 'zero';
    }

    private readonly position = computed(() => this.form.position?.().value());
    private readonly isZeroWidget = computed<boolean>(() => this.form.value().type === 'zero');

    // --- CSS custom property values (bound on the group wrapper in the template) ---
    public readonly groupZIndex = computed<string | null>(() => {
        const zIndex = this.position()?.zIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupZIndexMobile = computed<string | null>(() => {
        const position = this.position();
        const zIndex = this.form.isSynchronized() ? position?.zIndex : position?.mobileZIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupTransform = computed<string>(() =>
        this.isZeroWidget() ? 'none' : rotateVar(this.position()?.rotate)
    );
    public readonly groupTransformMobile = computed<string>(() =>
        this.isZeroWidget() ? 'none' : rotateVar(this.position()?.mobileRotate)
    );
    public readonly groupMargin = computed<string>(() =>
        this.isZeroWidget() ? '0' : CONTAINER_INNER_MARGIN[this.form.horizontalIndent().value()]
    );
    public readonly itemMargin = computed<string>(() =>
        this.isZeroWidget() ? '0' : BUTTON_OUTER_MARGIN[this.form.horizontalIndent().value()]
    );
    public readonly groupJustify = computed<string>(() => flexAlignVar(this.form.horizontalAlign().value()));
    public readonly groupJustifyMobile = computed<string>(() =>
        flexAlignVar(this.form.mobileHorizontalAlign().value() ?? this.form.horizontalAlign().value())
    );
    public readonly groupJustifyTablet = computed<string>(() =>
        flexAlignVar(this.form.tabletHorizontalAlign().value() ?? this.form.horizontalAlign().value())
    );

    // Per-item variable maps, one entry per rendered button.
    public readonly buttonsCssVars = computed(() => {
        const widget = this.form.value();
        return widget.data.buttons.map((button: ButtonItem) => getButtonItemCssVars(widget, button, themeColorCss));
    });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<ButtonForm>(this.widget().id, this.destroyRef);

        if (this.isZero) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block buttons auto-grow with their content: the rendered anchor's
     * height is mirrored back into the extended-position grid so the zero
     * host's selection frame and boundary math track the real size. Replaces
     * the FormGroup view's `valueChanges → getComputedStyle → setHeight`
     * subscription (with its `skipValueChange` re-entrancy guard) — here the
     * change-comparison before each write breaks the feedback loop instead.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroHeightFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroHeightFromDom(): void {
        const anchor = this.elementRef.nativeElement.querySelector('a') as HTMLAnchorElement | null;
        if (!anchor) return;

        const height = parseFloat(getComputedStyle(anchor).height);
        if (!Number.isFinite(height) || height <= 0) return;

        const zeroForm = this.form as ButtonZeroForm;
        const position = zeroForm.position().value();

        if (this.isDeviceMobileSig()) {
            if (position.mobileHeight !== height) zeroForm.position.mobileHeight().value.set(height);
            return;
        }

        if (this.isDeviceTabletSig()) {
            if (position.tabletHeight !== height) zeroForm.position.tabletHeight().value.set(height);
            return;
        }

        const writeAll = () => {
            if (position.mobileHeight !== height) zeroForm.position.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) zeroForm.position.tabletHeight().value.set(height);
            if (position.height !== height) zeroForm.position.height().value.set(height);
        };

        // Batch the three device writes into one store dispatch — unless an
        // outer transaction (settings panel editing) is already open: the
        // plugin doesn't support nesting, a nested `transact` would commit
        // the outer snapshot prematurely.
        if (zeroForm.inTransaction()) {
            writeAll();
        } else {
            zeroForm.transact(writeAll);
        }
    }

    public getButtonEffectClass(button: ButtonItem): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonClass(button: ButtonItem): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[button.size]}`;

        if (button.styleType === EButtonStyleType.custom) {
            return `${styles} ${getButtonItemCssClasses(button)} ${this.getButtonEffectClass(button)}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[button.styleType as keyof typeof BUTTON_TYPE_CLASS_MAP]} ${getButtonItemCssClasses(
            button
        )} ${this.getButtonEffectClass(button)}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }
}
